// Lib
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

type BackgroundRemovalWipeOverlay = {
    dataUrl: string;
    renderKey: number;
    wiping: boolean;
    // The rotate/flip transform the outgoing image was displayed with, so the wipe frame mirrors the cropper
    // instead of showing the raw (un-flipped) source pixels.
    transform: string;
};

type PendingWipeOverlay = {
    dataUrl: string;
    transform: string;
};

type UseBackgroundRemovalWipeOverlayArgs = {
    enabled: boolean;
    deferWipeMount?: boolean;
    editorImageLoadKey: string;
    editorIsReady: boolean;
    imageTransform: string;
    onWipeSettled?: () => void;
};

/**
 * Wipes the previously-loaded editor image away once the replacement cropper image is ready.
 */
export const useBackgroundRemovalWipeOverlay = ({
    enabled,
    deferWipeMount = false,
    editorImageLoadKey,
    editorIsReady,
    imageTransform,
    onWipeSettled,
}: UseBackgroundRemovalWipeOverlayArgs) => {
    const lastLoadedDataUrlRef = useRef<string | null>(null);
    const previousEditorImageLoadKeyRef = useRef(editorImageLoadKey);
    const wipeRenderKeyRef = useRef(0);
    const pendingWipeRef = useRef<PendingWipeOverlay | null>(null);
    const wipeActiveRef = useRef(false);
    const onWipeSettledRef = useRef(onWipeSettled);
    onWipeSettledRef.current = onWipeSettled;
    const [wipeOverlay, setWipeOverlay] = useState<BackgroundRemovalWipeOverlay | null>(null);

    const mountWipeOverlay = useCallback((dataUrl: string, transform: string) => {
        wipeActiveRef.current = true;
        wipeRenderKeyRef.current += 1;
        setWipeOverlay({ dataUrl, renderKey: wipeRenderKeyRef.current, wiping: false, transform });
    }, []);

    useLayoutEffect(() => {
        const previousEditorImageLoadKey = previousEditorImageLoadKeyRef.current;
        previousEditorImageLoadKeyRef.current = editorImageLoadKey;

        if (previousEditorImageLoadKey === editorImageLoadKey) return;
        if (!enabled) return;
        if (!lastLoadedDataUrlRef.current) return;

        const nextWipe = {
            dataUrl: lastLoadedDataUrlRef.current,
            transform: imageTransform,
        };

        if (deferWipeMount) {
            pendingWipeRef.current = nextWipe;
            return;
        }

        pendingWipeRef.current = null;
        mountWipeOverlay(nextWipe.dataUrl, nextWipe.transform);
    }, [deferWipeMount, enabled, editorImageLoadKey, imageTransform, mountWipeOverlay]);

    useLayoutEffect(() => {
        if (deferWipeMount) return undefined;

        if (pendingWipeRef.current) {
            const pending = pendingWipeRef.current;
            pendingWipeRef.current = null;
            mountWipeOverlay(pending.dataUrl, pending.transform);
            return undefined;
        }

        let secondFrame = 0;
        const firstFrame = requestAnimationFrame(() => {
            secondFrame = requestAnimationFrame(() => {
                if (wipeActiveRef.current) return;
                onWipeSettledRef.current?.();
            });
        });

        return () => {
            cancelAnimationFrame(firstFrame);
            if (secondFrame) cancelAnimationFrame(secondFrame);
        };
    }, [deferWipeMount, mountWipeOverlay]);

    useEffect(() => {
        if (!editorIsReady) return undefined;
        if (!wipeOverlay) return undefined;
        if (wipeOverlay.wiping) return undefined;

        const { renderKey } = wipeOverlay;

        // useEffect runs after the overlay is committed, so the initial clip-path is in place before the wipe class is added.
        setWipeOverlay((current) => (current?.renderKey === renderKey ? { ...current, wiping: true } : current));
    }, [editorIsReady, wipeOverlay]);

    const handleImageDataUrlLoad = useCallback((dataUrl: string) => {
        lastLoadedDataUrlRef.current = dataUrl;
    }, []);

    const handleWipeAnimationEnd = useCallback(() => {
        wipeActiveRef.current = false;
        setWipeOverlay((current) => (current?.wiping ? null : current));
        onWipeSettledRef.current?.();
    }, []);

    return {
        handleImageDataUrlLoad,
        handleWipeAnimationEnd,
        wipeOverlay,
    };
};
